import { Hex, pad, toHex } from 'viem'

import {
  deriveStateChangeResult,
  ERC20_TRANSFER_TOPIC,
} from '~/components/txn-display/state-change/utils/deriveStateChangeResult'
import { getWethTransferLogs } from '~/components/txn-display/state-change/utils/simulation'
import { traceTransactionOnServer } from '~/components/txn-display/state-change/utils/simulationClient'
import { WAREHOUSE_NATIVE_TOKEN } from '~/constants/addresses'
import { getChain, SupportedChainId } from '~/constants/chains'
import { EvmAddress } from '~/utils/address'
import { getReadPublicClient } from '~/utils/requests'

import type { Log, PublicClient } from 'viem'
import type {
  CombinedStateChangeResult,
  CustomLog,
  TraceCall,
} from '~/components/txn-display/state-change/types'
import type { IAddress } from '~/types'

export const getPastTxnStateChange = async (transaction: {
  transactionHash: Hex
  chainId: number
  addressesToWatch: IAddress[]
}): Promise<CombinedStateChangeResult | undefined> => {
  if (!transaction.transactionHash) return undefined

  const client = getReadPublicClient(transaction.chainId)

  const hash = transaction.transactionHash as Hex
  const receipt = await client.getTransactionReceipt({ hash }).catch(() => null)
  if (!receipt) return undefined

  const trace = await getTransactionTrace(client, hash, transaction.chainId)

  const baseLogs = toCustomLogs(receipt.logs)
  const nativeTransferLogs = buildNativeTransferLogs(trace)
  const wethLogs = getWethTransferLogs(baseLogs)

  const logs = [...baseLogs, ...nativeTransferLogs, ...wethLogs]

  if (!logs.length || !transaction.addressesToWatch.length) return undefined

  return deriveStateChangeResult({
    addressesToWatch: transaction.addressesToWatch,
    chainId: transaction.chainId as SupportedChainId,
    simulateCallsData: logs,
  })
}

const toCustomLogs = (logs?: Log[]): CustomLog[] =>
  (logs ?? []).map((log) => ({
    address: EvmAddress.format(log.address),
    data: log.data as Hex,
    topics: log.topics as [Hex, ...Hex[]],
  }))

// Walks the call trace to find native ETH transfers.
// Only includes CALL types - DELEGATECALL/STATICCALL don't actually move ETH.
const buildNativeTransferLogs = (root?: TraceCall): CustomLog[] => {
  if (!root) return []
  const entries: CustomLog[] = []

  const walk = (call?: TraceCall) => {
    if (!call) return

    const from = call.from
      ? EvmAddress.format(call.from as IAddress)
      : undefined
    const to = call.to ? EvmAddress.format(call.to as IAddress) : undefined
    const value = normalizeValue(call.value)
    const callType = (call.type ?? '').toUpperCase()
    const isCall = callType === 'CALL'

    if (from && to && value && value !== 0n && isCall) {
      entries.push({
        address: EvmAddress.format(WAREHOUSE_NATIVE_TOKEN),
        topics: [ERC20_TRANSFER_TOPIC, pad(from), pad(to)],
        data: pad(toHex(value)),
      })
    }

    call.calls?.forEach(walk)
  }

  walk(root)
  return entries
}

const getTransactionTrace = async (
  client: PublicClient,
  hash: Hex,
  chainId: number,
) => {
  try {
    const trace = (await client.transport.request({
      method: 'debug_traceTransaction',
      params: [hash, { tracer: 'callTracer' }],
    })) as TraceCall | undefined

    if (trace) return trace
  } catch {
    // fall through to tenderly
  }

  if (!getChain(chainId)?.supportsTenderly) return undefined

  return traceTransactionOnServer({ chainId, transactionHash: hash })
}

const normalizeValue = (value: TraceCall['value']) => {
  if (typeof value === 'bigint') return value
  if (typeof value === 'string') {
    try {
      return BigInt(value)
    } catch {
      return undefined
    }
  }
  if (typeof value === 'number') return BigInt(value)
  return undefined
}
